<template>
  <div class="border border-grey-lighter p-12 flex flex-col sm:flex-row mb-24" style="border-radius: 20px;">
    <div class="cover-block mr-0 sm:mr-16">
      <img class="cover w-full" :src="rooms.images[0] ? rooms.images[0] : require('@/assets/images/default-room.jpg')" :alt="rooms.images[0]" style="border-radius: 14px;">
    </div>
    <div class="flex flex-col w-full">
      <div class="plan-detail" v-for="(plan, index) in rooms.plans" :key="`plan-${index}`">
        <div class="flex items-center justify-between">
          <h4 class="font-bold capitalize text-base">{{ rooms.name }}</h4>
          <p v-if="!hasAddon" class="text-sm font-medium text-grey-dark cursor-pointer" @click="openPlanCancelPolicy(index)">{{ $t('cancelPolicy') }}</p>
        </div>
        <div class="flex flex-col sm:flex-row justify-between">
          <div class="flex-auto leading-md text-sm">
            <div v-if="plan.name" class="text-grey-darker text-sm">
              {{ plan.name }}
            </div>
            <div class="my-16">
              <div v-if="hasAddon" class="flex items-center text-red">
                <i class="owl-status-ban" style="font-size: 15px; margin-left: -.1em;"></i>
                <div class="text-sm font-medium">{{ $t('bid.noncancelability') }}</div>
              </div>
              <div v-if="plan.cancelPolicy && plan.cancelPolicy.lastFreeCancelDate && !hasAddon" class="flex items-center">
                <img class="mr-4" height="15" width="15" src="@/assets/images/icon-check.svg" alt="check">
                <div class="text-sm font-medium leading-normal text-grey-dark" v-html="$t('cancelForFree', {before: $options.filters.toDate(plan.cancelPolicy.lastFreeCancelDate, 'LLL') })" />
              </div>
              <template v-if="isExternalSource">
                <!-- 外部房源 -->
                <template v-if="plan.planMeal && plan.planMeal.key !== '0'">
                  <div :class="['flex items-center font-medium', plan.planMeal.key !== 6 ? 'text-green-darker' : 'text-grey-darker']">
                    <div class="rounded-circle flex items-center justify-center">
                      <img class="mr-4" height="16" width="16" :src="plan.planMeal.key !== 6 ? require('@/assets/images/icon-breakfast.svg') : require('@/assets/images/icon-no-breakfast.svg')" alt="breakfast">
                    </div>
                    <span>{{ plan.planMeal.text }}</span>
                  </div>
                </template>
                <!-- /外部房源 -->
              </template>
              <template v-else>
                <!-- 非外部房源 -->
                <div :class="['flex items-center font-medium', plan.isProvideBreakfast ? 'text-green-darker' : 'text-grey-darker']">
                  <div class="rounded-circle flex items-center justify-center">
                    <img class="mr-4" height="16" width="16" :src="plan.isProvideBreakfast ? require('@/assets/images/icon-breakfast.svg') : require('@/assets/images/icon-no-breakfast.svg')" alt="breakfast">
                  </div>
                  <span>{{ plan.isProvideBreakfast ? $t('hasBreakfast') : $t('noBreakfast') }}</span>
                </div>
                <!-- /非外部房源 -->
              </template>
            </div>
            <div>
              <span class="font-bold">{{ $t('orderCard.title.itemPrice') }}：</span>
              <span v-if="isCurrencyConvert" class="text-sm">{{ $t('priceAbout') }} </span>{{ currencySign }}
                {{ plan.untaxedAvgPrice | currencyConvert({inputCurrency: order.currency}) }}
                ({{ $t('excludeTax') }}) / {{ $tc('night') }}
            </div>
            <div>
              <span class="font-bold">{{ $t('orderCard.title.roomCount') }}：</span>
              <span>{{ plan.count }} {{ rooms.unit }}</span>
            </div>
            <div v-if="isAddBedDisplay">
              <span class="font-bold">{{ $t('addBedService') }}：</span>
              <span>{{ currencySign }} {{ planPriceTotal(plan) | currencyConvert({inputCurrency: order.hotel.currency, listInteger: true}) | toCurrency }}</span>
              <span class="text-xs ml-8">(<template v-if="plan.bed && plan.bed.adult">{{ $t('adult') }}x{{ plan.bed.adult }}</template>
                <template v-if="plan.bed && plan.bed.adult && plan.bed.child">, </template>
                <template v-if="plan.bed && plan.bed.child">{{ $t('child') }}x{{ plan.bed.child }}</template>)
              </span>
            </div>
            <div>
              <span class="font-bold">{{ $t('orderCard.title.orderPrice') }}：</span>
              <span v-if="isCurrencyConvert" class="text-sm">{{ $t('priceAbout') }} </span>{{ currencySign }}
              {{ plan.untaxedAvgPrice * totalNights * plan.count | currencyConvert({inputCurrency: order.currency}) }}
              ({{ $t('excludeTax') }})
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import numeral from 'numeral'
import fullPageModal from '@/components/modals/fullPageModal'
import roomDetail from '@/components/roomDetail'
import cancelPolicy from '@/components/cancelPolicyNew'

export default {
  name: 'orderDetailCardNew',
  props: {
    order: {
      type: Object,
      required: true
    },
    rooms: {
      type: [Object, Array],
      required: true
    }
  },
  computed: {
    ...mapGetters({
      currencySign: 'user/currencySign',
      'user/selectedCurrency': 'user/selectedCurrency'
    }),
    isCurrencyConvert: function () {
      return this.order.currency !== this['user/selectedCurrency']
    },
    totalNights: function () {
      const start = this.$dayjs(this.order.startAt)
      const end = this.$dayjs(this.order.endAt)
      return end.diff(start, 'days')
    },
    isExternalSource: function () {
      return this.order?.source !== 'owljourney'
    },
    isAddBedDisplay: function () {
      let count = 0
      for (const plan of this.rooms.plans) {
        for (const type in plan.bed) {
          count += plan.bed[type]
        }
      }
      return count > 0
    },
    addPeopleTotal: function () {
      let count = 0
      if (this.order.hotel.extraBedRules) {
        let price = {
          adult: this.order.hotel.extraBedRules.adult.isCharge ? this.order.hotel.extraBedRules.adult.price : 0,
          child: this.order.hotel.extraBedRules.child.isCharge ? this.order.hotel.extraBedRules.child.price : 0,
          infant: 0
        }
        for (const plan of this.rooms.plans) {
          for (const type in plan.bed) {
            count += plan.bed[type] * price[type] * this.totalNights
          }
        }
      }
      return count
    },
    hasAddon () {
      return this.order.extraAddons.length
    }
  },
  methods: {
    ...mapActions([
      'order/onPlanChange'
    ]),
    itemTotal: function (plan, order) {
      try {
        const untaxedAvgPrice = Number(numeral(plan.untaxedAvgPrice).format('0.00'))
        const planTotal = Number(numeral(this.$options.filters.currencyConvert(untaxedAvgPrice, { inputCurrency: order.hotel.currency, listInteger: true })).format('0.00')) * this.totalNights * plan.count
        const addBedsTotal = Number(numeral(this.$options.filters.currencyConvert(this.bedTotal(plan), { inputCurrency: order.hotel.currency, listInteger: true })).format('0.00'))

        return numeral(addBedsTotal + planTotal).format('0[.]00')
      } catch (e) {
        console.log('Error', e)
      }
    },
    bedTotal: function (item) {
      if (this.isAddBedDisplay) {
        const adultTotal = this.order.hotel.extraBedRules.adult.price * item.bed.adult * this.totalNights
        const childTotal = this.order.hotel.extraBedRules.child.price * item.bed.child * this.totalNights
        const total = adultTotal + childTotal
        if (!total) {
          // return this.$t('free')
          return 0
        }
        return total
      }
      return 0
    },
    isAddBed: function (plan) {
      if (plan?.bed) {
        return plan.bed.adult + plan.bed.child > 0
      }
      return false
    },
    removePlan: function () {
      this['order/onPlanChange']({
        hotel: this.order,
        room: this.rooms,
        plan: this.rooms.plan,
        count: 0
      })
    },
    openRoomDetail: function () {
      this.$modal.show(fullPageModal, {
        // 輸入內容
        context: roomDetail,
        rooms: {
          hotel: this.order,
          room: this.rooms
        }
      },
      {
        adaptive: true,
        name: 'roomDetail',
        // clickToClose: false,
        height: '90%',
        width: '90%',
        maxWidth: 1440,
        scrollable: true
      })
    },
    openPlanCancelPolicy: function (index) {
      window.dataLayer.push({
        event: 'order_cancellation'
      })
      this.$modal.show(fullPageModal, {
        // 輸入內容
        context: cancelPolicy,
        data: this.rooms.plans[index].cancelPolicy,
        from: 'orderDetail',
        title: this.$t('cancelPolicy')
      },
      {
        adaptive: true,
        name: 'cancelPolicy',
        // clickToClose: false,
        height: '90%',
        width: '90%',
        maxWidth: 811,
        minWidth: 345,
        maxHeight: 650,
        scrollable: true
      })
    },
    planPriceTotal: function (plan) {
      let count = 0
      if (this.order.hotel.extraBedRules) {
        let price = {
          adult: this.order.hotel.extraBedRules.adult.isCharge ? this.order.hotel.extraBedRules.adult.price : 0,
          child: this.order.hotel.extraBedRules.child.isCharge ? this.order.hotel.extraBedRules.child.price : 0,
          infant: 0
        }
        for (const type in plan.bed) {
          count += plan.bed[type] * price[type] * this.totalNights
        }
      }
      return count
    }
  },
  mounted () {
  }
}
</script>

<style lang="sass" scoped>
  .room
    &__card
      @apply rounded border
      @screen md
        & + &
          @apply border-t-0 rounded-none
      &:not(:last-of-type)
        @apply mb-16
        @screen md
          @apply border-b border-grey-lightest mb-0
      &__cover
        figure
          @apply w-full hidden
          height: 200px
          @screen md
            @apply block
            width: 175px
            height: 102px
      &__breakfast
        @apply mt-16 px-16
        i
          width: 24px
          height: 24px
          @apply bg-green text-white rounded-full mr-8
  .room__block
    @apply flex
    & + &
      @apply border-t-1 border-grey-lightest
  .room__info
    @apply flex flex-col text-sm mb-16
    @screen sm
      @apply flex-row items-center mb-8
  .room__title
    @apply flex-no-shrink leading-md flex-1 mb-4
    @apply font-bold
  .room__desc
    @apply text-grey-dark text-sm flex-1
    &--plan
      @apply text-blue
  .plan-detail
    &:not(:first-child)
      @apply mt-16 border-t border-grey-lighter pt-16
  .cover-block
    @apply w-full
    @screen sm
      min-width: 190px
      width: 190px
</style>
